import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { PROJETS, type Projet } from "@/data/projets";
import { PortfolioLayout } from "./PortfolioLayout";
import { PROJECT_CONTEXT } from "./portfolio-content";
import "./portfolio-projects-carnet.css";

const projectOrder = ["gridar", "sendmenow", "finditnow", "publiar", "dkbois", "housing-ai"];
const projects = [...PROJETS].sort((a, b) => projectOrder.indexOf(a.slug) - projectOrder.indexOf(b.slug));
const filters = [
  { key: "all", label: "Tous" },
  { key: "client", label: "Projets d'entreprise" },
  { key: "product", label: "Produits personnels" },
] as const;
type Filter = typeof filters[number]["key"];
const matchesFilter = (project: Projet, filter: Filter) => filter === "all" || project.client === (filter === "client");
const projectLabel = (project: Projet) => PROJECT_CONTEXT[project.slug]?.label ?? (project.client ? "Mandat client" : "Produit personnel");

function ProjectImage({ project, featured = false }: { project: Projet; featured?: boolean }) {
  return (
    <Link className="pf-carnet-image" to={`/projets/${project.slug}`} aria-label={`Découvrir le projet ${project.title}`}>
      <img
        src={`/projets/${project.mockup}-760.webp`}
        srcSet={`/projets/${project.mockup}-760.webp 760w, /projets/${project.mockup}-1100.webp 1100w`}
        sizes={featured ? "(min-width: 1800px) 1040px, (min-width: 900px) 62vw, 92vw" : "(min-width: 1800px) 790px, (min-width: 700px) 45vw, 92vw"}
        alt={`Capture de l'interface de ${project.title}`}
        loading={project.slug === "gridar" ? "eager" : "lazy"}
        decoding="async"
      />
    </Link>
  );
}

function ProjectContext({ project }: { project: Projet }) {
  const context = PROJECT_CONTEXT[project.slug];
  return <dl className="pf-carnet-context"><div><dt>Le besoin</dt><dd>{context.purpose}</dd></div><div><dt>Mon travail</dt><dd>{context.work}</dd></div></dl>;
}

function ProjectWebsite({ project }: { project: Projet }) {
  return <a className="pf-carnet-website" href={project.url} target="_blank" rel="noopener noreferrer" aria-label={`Ouvrir le site ${project.title} dans un nouvel onglet`}>Ouvrir le site <ArrowUpRight size={14} aria-hidden="true" /></a>;
}

function FeaturedProject({ project }: { project: Projet }) {
  return (
    <article className={`pf-carnet-feature${project.slug === "sendmenow" ? " pf-carnet-feature-reverse" : ""}`} data-project={project.slug} aria-labelledby={`project-${project.slug}`}>
      <div className="pf-carnet-feature-copy">
        <div className="pf-carnet-feature-heading">
          <span className="pf-carnet-number" aria-hidden="true">{String(projectOrder.indexOf(project.slug) + 1).padStart(2, "0")}</span>
          <div><h2 id={`project-${project.slug}`}>{project.title}</h2><span className="pf-carnet-badge">{projectLabel(project)}</span></div>
        </div>
        <ProjectContext project={project} />
        <p className="pf-carnet-stack">{project.stack.join(" / ")}</p>
        <div className="pf-carnet-actions"><Link className="pf-button pf-carnet-project-link" to={`/projets/${project.slug}`}>Voir le projet <ArrowUpRight size={18} aria-hidden="true" /></Link><ProjectWebsite project={project} /></div>
      </div>
      <ProjectImage project={project} featured />
    </article>
  );
}

function ProjectCard({ project }: { project: Projet }) {
  return (
    <article className="pf-carnet-card" data-project={project.slug} aria-labelledby={`project-${project.slug}`}>
      <ProjectImage project={project} />
      <p className="pf-carnet-category">{projectLabel(project)}</p>
      <h3 id={`project-${project.slug}`}>{project.title}</h3>
      <p className="pf-carnet-tagline">{project.tagline}</p>
      <div className="pf-carnet-card-bottom"><p className="pf-carnet-stack">{project.stack.join(" / ")}</p><Link className="pf-text-link pf-carnet-project-link" to={`/projets/${project.slug}`}>Voir le projet <ArrowUpRight size={16} aria-hidden="true" /></Link></div>
      <details className="pf-carnet-detail"><summary>Le besoin et mon travail <span className="sr-only">sur {project.title}</span><Plus size={16} aria-hidden="true" /></summary><div><ProjectContext project={project} /><ProjectWebsite project={project} /></div></details>
    </article>
  );
}

export default function PortfolioProjectsPage() {
  const [filter, setFilter] = useState<Filter>("all");
  const visible = projects.filter(project => matchesFilter(project, filter));
  const featured = visible.filter(project => projectOrder.indexOf(project.slug) < 2);
  const more = visible.filter(project => projectOrder.indexOf(project.slug) >= 2);

  return (
    <PortfolioLayout carnet path="/projets" title="Réalisations | Tokam Darius, développeur web à Jonquière" description="Les applications web que j'ai conçues et codées : FDKbois, Send Me Now, Find It Now, Gridar, LocaSur, Publiar. Django, React, TypeScript.">
      <section className="pf-carnet-hero" aria-labelledby="projects-title">
        <div><p className="pf-carnet-eyebrow">02 / Carnet de réalisations</p><h1 id="projects-title">Ce que<br /><span>j'ai construit.</span></h1><p className="pf-carnet-intro">Six réalisations pour découvrir ma façon de résoudre<br className="pf-carnet-desktop-break" /> un problème et de construire une application.</p></div>
        <aside className="pf-carnet-aside"><p>Le besoin<br />Le travail<br />Le projet</p><span aria-hidden="true" /><p>Des idées aux interfaces, en passant par la technique, voici des projets concrets et utiles.</p></aside>
      </section>

      <section className="pf-carnet-work" aria-label="Réalisations">
        <div className="pf-carnet-filters" role="group" aria-label="Filtrer les projets">{filters.map(item => <button type="button" key={item.key} aria-pressed={filter === item.key} aria-controls="carnet-results" onClick={() => setFilter(item.key)}>{item.label}<span>{String(projects.filter(project => matchesFilter(project, item.key)).length).padStart(2, "0")}</span></button>)}</div>
        <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{visible.length} projets affichés</p>
        <div id="carnet-results">
          <div className="pf-carnet-featured">{featured.map(project => <FeaturedProject key={project.slug} project={project} />)}</div>
          {more.length > 0 && <section className="pf-carnet-more" aria-labelledby="more-projects-title"><div className="pf-carnet-more-heading"><h2 id="more-projects-title">À explorer aussi</h2><p>{filter === "all" ? "Quatre autres projets, toujours avec la même exigence." : "D'autres projets, toujours avec la même exigence."}</p></div><div className="pf-carnet-grid">{more.map(project => <ProjectCard key={project.slug} project={project} />)}</div></section>}
        </div>
      </section>

      <section className="pf-carnet-contact" aria-labelledby="projects-contact-title"><h2 id="projects-contact-title">Un projet en tête ? <span>Parlons-en.</span></h2><p>Une idée, un besoin ou une question ?<br />Je suis toujours ouvert à en discuter.</p><Link className="pf-button" to="/contact">Me contacter <ArrowUpRight size={18} aria-hidden="true" /></Link></section>
    </PortfolioLayout>
  );
}
